import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import CategorySelect, { flattenGroups } from '../components/CategorySelect';
import { isWissenEmail } from '../utils/validate';
import { MemberDetail, PodDetail, QuestDetail, Avatar } from '../components/DetailViews';
import { PageTurn, Paginate } from '../components/Paginate';

const PAGE_TABLE = 10;
const PAGE_GRID = 12;

// ── LEADERBOARD ──────────────────────────────────────────────────────────
export function Leaderboard() {
  const [board, setBoard] = useState([]);
  const [openMember, setOpenMember] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get('/users/leaderboard').then(r => setBoard(r.data)).catch(() => {}); }, []);
  const tierColors = { Probation: '#4fc3f7', Core: '#00ff88', Veteran: '#ffd700', Legend: '#ff6b35' };
  const medals = ['🥇', '🥈', '🥉'];
  const [page, setPage] = useState(0);
  const pagedBoard = board.slice(page * PAGE_TABLE, (page + 1) * PAGE_TABLE);
  return (
    <div>
      <div style={{ marginBottom: 24 }}><div className="page-title">LEADERBOARD</div><div className="page-sub">Live rankings · Click a member for their analytics</div></div>
      <PageTurn page={page}>
      <div className="card">
        <table className="table"><thead><tr><th>RANK</th><th>MEMBER</th><th>TIER</th><th>WEEKLY PTS</th><th>TOTAL PTS</th><th>STATUS</th></tr></thead>
          <tbody>{pagedBoard.map((m, i) => {
            const rank = page * PAGE_TABLE + i;
            return (
            <tr key={m.id} onClick={() => setOpenMember(m.id)} style={{ cursor: 'pointer', background: m.id === user?.id ? 'rgba(0,212,255,0.05)' : 'transparent' }}>
              <td><span style={{ fontFamily: 'Orbitron', fontSize: 14, fontWeight: 800, color: rank < 3 ? 'var(--j4)' : 'var(--txt3)' }}>{medals[rank] || `#${rank + 1}`}</span></td>
              <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar user={m} size={30} />
                <div>
                  <div style={{ fontWeight: 600, color: m.id === user?.id ? 'var(--j1)' : 'var(--txt)' }}>{m.name}{m.id === user?.id && ' (you)'}</div>
                  <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{m.email}</div>
                </div></div></td>
              <td><span className={`tier-badge tier-${m.tier?.toLowerCase()}`}>{m.tier}</span></td>
              <td><span style={{ fontFamily: 'Orbitron', color: 'var(--j1)' }}>{(m.weeklyPoints || 0).toLocaleString()}</span></td>
              <td><span style={{ fontFamily: 'Orbitron', fontSize: 15, fontWeight: 800, color: tierColors[m.tier] }}>{(m.totalPoints || 0).toLocaleString()}</span></td>
              <td><span style={{ width: 8, height: 8, borderRadius: '50%', background: m.active ? 'var(--j5)' : 'var(--j6)', display: 'inline-block' }} /></td>
            </tr>
            );
          })}</tbody>
        </table>
        {board.length === 0 && <div className="empty-state">Loading leaderboard...</div>}
      </div>
      </PageTurn>
      <Paginate page={page} total={board.length} pageSize={PAGE_TABLE} onPage={setPage} />
      <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {[{label:'Weekly Cap',val:'1,500 pts'},{label:'Mentor Bonus',val:'8% of mentee'},{label:'Pilot Duration',val:'90 days'},{label:'Adoption Gate',val:'60-day check'}].map(s => (
          <div key={s.label} className="card" style={{ flex: '1 1 180px' }}><div className="stat-val" style={{ fontSize: 20 }}>{s.val}</div><div className="stat-label">{s.label}</div></div>
        ))}
      </div>
      {openMember && <MemberDetail id={openMember} onClose={() => setOpenMember(null)} />}
    </div>
  );
}

// ── QUEST BOARD ────────────────────────────────────────────────────────────
export function QuestBoard() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [quests, setQuests] = useState([]);
  const [openQuest, setOpenQuest] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', stream: 'individual', bountyMin: 100, bountyMax: 600, deadline: '', tags: '', multiplierEligible: [] });
  const [toast, setToast] = useState(null);

  useEffect(() => { api.get('/quests').then(r => setQuests(r.data)).catch(() => {}); }, []);

  const filtered = filter === 'all' ? quests : quests.filter(q => q.stream === filter || q.status === filter);
  const [page, setPage] = useState(0);
  const pagedFiltered = filtered.slice(page * PAGE_GRID, (page + 1) * PAGE_GRID);

  const claim = async (id) => {
    try {
      const { data } = await api.patch(`/quests/${id}/claim`);
      setQuests(q => q.map(quest => quest.id === id ? data : quest));
      showToast('Quest claimed! Get building.', 'success');
    } catch (e) { showToast(e.response?.data?.error || 'Failed to claim', 'error'); }
  };

  const addQuest = async () => {
    try {
      const { data } = await api.post('/quests', { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) });
      setQuests(q => [data, ...q]);
      setShowForm(false);
      showToast('Quest added to the board!', 'success');
    } catch { showToast('Failed to add quest', 'error'); }
  };

  const showToast = (msg, type) => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const streamColor = { individual: 'var(--j1)', pod: 'var(--j3)', hybrid: 'var(--j4)' };

  return (
    <div>
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div><div className="page-title">QUEST BOARD</div><div className="page-sub">Claim quests · Earn bounties · Build the future</div></div>
        {can('cwg', 'sme', 'stakeholder') && <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ ADD QUEST</button>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all','open','individual','pod','hybrid','claimed'].map(f => (
          <button key={f} onClick={() => { setFilter(f); setPage(0); }} className="btn" style={{ background: filter === f ? 'rgba(0,212,255,0.1)' : 'transparent', borderColor: filter === f ? 'var(--j1)' : 'var(--brd2)', color: filter === f ? 'var(--j1)' : 'var(--txt2)' }}>{f.toUpperCase()}</button>
        ))}
      </div>
      <PageTurn page={page}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {pagedFiltered.map(q => (
          <div key={q.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 4, lineHeight: 1.3, cursor: 'pointer' }} onClick={() => setOpenQuest(q)} title="View details & charts">{q.title}</div>
                <span style={{ fontSize: 9, padding: '2px 6px', border: '1px solid', borderRadius: 2, color: streamColor[q.stream], borderColor: streamColor[q.stream] + '50', background: streamColor[q.stream] + '10', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>{q.stream?.toUpperCase()}</span>
              </div>
              <span className={`pill pill-${q.status}`}>{q.status}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--txt2)', lineHeight: 1.5, marginBottom: 12 }}>{q.description}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
              {(q.tags || []).map(t => <span key={t} style={{ fontSize: 9, padding: '2px 6px', background: 'rgba(0,212,255,0.05)', border: '1px solid var(--brd2)', borderRadius: 2, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{t}</span>)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 800, color: 'var(--j1)' }}>{q.bountyMin?.toLocaleString()}–{q.bountyMax?.toLocaleString()} <span style={{ fontSize: 10, fontWeight: 400 }}>pts</span></div>
                <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Due: {new Date(q.deadline).toLocaleDateString()}</div>
              </div>
              {q.status === 'open' && can('member', 'sme') && <button className="btn btn-primary btn-sm" onClick={() => claim(q.id)}>CLAIM</button>}
              {q.status === 'claimed' && q.claimedBy === user?.id && (
                <button className="btn btn-success btn-sm" onClick={() => navigate(`/app/contributions?quest=${q.id}`)}>SUBMIT CONTRIBUTION</button>
              )}
              {q.status === 'claimed' && q.claimedBy !== user?.id && <span style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>CLAIMED BY {q.claimedByName?.toUpperCase()}</span>}
              {q.status === 'in_review' && <span style={{ fontSize: 10, color: 'var(--j4)', fontFamily: 'Share Tech Mono' }}>● IN REVIEW</span>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="empty-state" style={{ gridColumn: '1/-1' }}>No quests found for filter: {filter}</div>}
      </div>
      </PageTurn>
      <Paginate page={page} total={filtered.length} pageSize={PAGE_GRID} onPage={setPage} />

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            <div className="modal-title">POST NEW QUEST</div>
            {['title','description'].map(f => (
              <div key={f} className="form-group">
                <label className="label">{f}</label>
                {f === 'description' ? <textarea className="input" rows={3} value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} /> : <input className="input" value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} />}
              </div>
            ))}
            <div className="grid-2">
              <div className="form-group"><label className="label">Stream</label>
                <select className="input" value={form.stream} onChange={e => setForm(p => ({ ...p, stream: e.target.value }))}><option value="individual">Individual</option><option value="pod">Pod</option><option value="hybrid">Hybrid</option></select></div>
              <div className="form-group"><label className="label">Deadline</label><input type="date" className="input" value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))} /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label className="label">Bounty Min (pts)</label><input type="number" className="input" value={form.bountyMin} onChange={e => setForm(p => ({ ...p, bountyMin: +e.target.value }))} /></div>
              <div className="form-group"><label className="label">Bounty Max (pts)</label><input type="number" className="input" value={form.bountyMax} onChange={e => setForm(p => ({ ...p, bountyMax: +e.target.value }))} /></div>
            </div>
            <div className="form-group"><label className="label">Tags (comma separated)</label><input className="input" value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="LLM, RAG, Agents..." /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowForm(false)}>CANCEL</button>
              <button className="btn btn-primary" onClick={addQuest}>POST QUEST</button>
            </div>
          </div>
        </div>
      )}
      {openQuest && <QuestDetail quest={openQuest} onClose={() => setOpenQuest(null)} />}
    </div>
  );
}

// ── CONTRIBUTIONS ─────────────────────────────────────────────────────────
const PEER_PARAMS = [
  { key: 'working',      label: 'Solution Works',       icon: '⚙', desc: 'Code / demo actually runs and functions as claimed' },
  { key: 'quality',      label: 'Code Quality',         icon: '✦', desc: 'Readable, well-structured, sensible naming & patterns' },
  { key: 'docs',         label: 'Documentation',        icon: '📝', desc: 'Inline comments, README, evidence clearly provided' },
  { key: 'completeness', label: 'Feature Completeness', icon: '🎯', desc: 'Matches the described functionality — nothing major missing' },
  { key: 'impact',       label: 'Impact & Reusability', icon: '🚀', desc: 'Real value to the team; others can adopt or build on this' },
];
const PEER_FLAGS = [
  { key: 'wrong_category',   label: 'Wrong Category',        icon: '🏷' },
  { key: 'code_broken',      label: 'Code / Demo Broken',    icon: '🚫' },
  { key: 'feature_deviated', label: 'Feature Deviated',      icon: '↩️' },
  { key: 'poor_docs',        label: 'Poor Docs / Comments',  icon: '📝' },
  { key: 'demo_broken',      label: 'Demo Link Down',        icon: '🔗' },
  { key: 'size_wrong',       label: 'Size Misclassified',    icon: '📏' },
  { key: 'duplicate',        label: 'Duplicate Effort',      icon: '🔁' },
];
const SME_PARAMS = [
  { key: 'depth',        label: 'Technical Depth',   icon: '🏗', desc: 'Deep understanding; not surface-level' },
  { key: 'architecture', label: 'Architecture',      icon: '⬡', desc: 'Sound design decisions, scalable, maintainable' },
  { key: 'quality',      label: 'Implementation',    icon: '✦', desc: 'Code quality, correctness, edge-case handling' },
  { key: 'innovation',   label: 'Innovation',        icon: '💡', desc: 'Novel approach, not a trivial rehash' },
  { key: 'docs',         label: 'Documentation',     icon: '📋', desc: 'Technical docs, comments, usage guide' },
];
const FLAG_LABELS = Object.fromEntries([...PEER_FLAGS].map(f => [f.key, `${f.icon} ${f.label}`]));
const BLANK_PEER_RATINGS = { working: 0, quality: 0, docs: 0, completeness: 0, impact: 0 };
const BLANK_SME_RATINGS  = { depth: 0, architecture: 0, quality: 0, innovation: 0, docs: 0 };

function StarRating({ value, onChange, size = 26, readonly = false }) {
  const [hovered, setHovered] = React.useState(0);
  return (
    <div style={{ display: 'flex', gap: 3, cursor: readonly ? 'default' : 'pointer' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span
          key={n}
          onMouseEnter={() => !readonly && setHovered(n)}
          onMouseLeave={() => !readonly && setHovered(0)}
          onClick={() => !readonly && onChange(n === value ? 0 : n)}
          style={{ fontSize: size, lineHeight: 1, color: n <= (hovered || value) ? '#ffd700' : 'var(--brd)', transition: 'color 0.1s', userSelect: 'none' }}
        >★</span>
      ))}
    </div>
  );
}

const PIPELINE_STAGES = [
  { key: 'submit', label: 'Submitted' },
  { key: 'peer', label: 'Peer Reviews' },
  { key: 'sme', label: 'SME Review' },
  { key: 'cwg', label: 'CWG Approval' },
  { key: 'adoption', label: 'Adoption Gate' },
];
function pipelineStageIdx(status) {
  const m = { submitted: 1, in_review: 1, peer_reviewed: 2, sme_approved: 3, pending_adoption: 3, approved: 4, adopted: 5 };
  return m[status] ?? (status === 'rejected' ? -1 : 1);
}
function PipelineMini({ status, peerCount = 0 }) {
  const idx = pipelineStageIdx(status);
  const rejected = status === 'rejected';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }} title={`Status: ${status}`}>
      {PIPELINE_STAGES.map((s, i) => {
        const stageNum = i + 1;
        const done = !rejected && idx > stageNum;
        const active = idx === stageNum;
        return (
          <React.Fragment key={s.key}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: rejected && active ? 'var(--j6)' : done ? 'var(--j5)' : active ? 'var(--j4)' : 'var(--brd2)', border: `1.5px solid ${rejected && active ? 'var(--j6)' : done ? 'var(--j5)' : active ? 'var(--j4)' : 'var(--brd)'}` }} title={s.label} />
            {i < PIPELINE_STAGES.length - 1 && <div style={{ width: 10, height: 1, background: done ? 'var(--j5)' : 'var(--brd)' }} />}
          </React.Fragment>
        );
      })}
      {rejected && <span style={{ fontSize: 9, color: 'var(--j6)', marginLeft: 4, fontFamily: 'Share Tech Mono' }}>REJECTED</span>}
    </div>
  );
}

export function Contributions() {
  const { user, can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contribs, setContribs] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [multipliers, setMultipliers] = useState([]);
  const [peerQueue, setPeerQueue] = useState([]);
  const [smeQueue, setSmeQueue] = useState([]);
  const [cwgQueue, setCwgQueue] = useState([]);
  const [adoptionQueue, setAdoptionQueue] = useState([]);
  const [linkedQuest, setLinkedQuest] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewForm, setReviewForm] = useState({ ratings: { ...BLANK_PEER_RATINGS }, flags: [], suggestedCategory: '', comment: '' });
  const [smeTarget, setSmeTarget] = useState(null);
  const [smeForm, setSmeForm] = useState({ smeScore: 85, notes: '', decision: 'approve', smeRatings: { ...BLANK_SME_RATINGS } });
  const [cwgTarget, setCwgTarget] = useState(null);
  const [cwgForm, setCwgForm] = useState({ finalPoints: '', notes: '' });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectForm, setRejectForm] = useState({ reason: '' });
  const [adoptTarget, setAdoptTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [githubResult, setGithubResult] = useState(null);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(0);
  const blankForm = { title: '', category: '', size: 'M', description: '', evidence: '', multipliers: [], questId: null, githubRepo: '', solutionLink: '' };
  const [form, setForm] = useState(blankForm);
  const [calc, setCalc] = useState(null);

  const loadQueues = () => {
    api.get('/contributions/peer-queue').then(r => setPeerQueue(r.data)).catch(() => {});
    if (can('sme', 'cwg')) api.get('/contributions/sme-queue').then(r => setSmeQueue(r.data)).catch(() => {});
    if (can('cwg', 'stakeholder')) api.get('/contributions/cwg-queue').then(r => setCwgQueue(r.data)).catch(() => {});
    if (can('cwg')) api.get('/contributions/adoption-queue').then(r => setAdoptionQueue(r.data)).catch(() => {});
  };

  useEffect(() => {
    Promise.all([api.get('/contributions'), api.get('/contributions/category-groups'), api.get('/contributions/multipliers')])
      .then(([c, cat, mult]) => { setContribs(c.data); setCategoryGroups(cat.data); setMultipliers(mult.data); }).catch(() => {});
    loadQueues();
  }, []);

  // If arriving from a claimed quest (?quest=<id>), pre-open the form linked to it.
  useEffect(() => {
    const questId = searchParams.get('quest');
    if (!questId) return;
    api.get('/quests').then(r => {
      const q = r.data.find(x => x.id === questId);
      if (q) {
        setLinkedQuest(q);
        setForm(f => ({ ...f, questId, title: f.title || q.title }));
        setShowForm(true);
      }
    }).catch(() => {});
  }, [searchParams]);

  const catPoints = useMemo(() => flattenGroups(categoryGroups), [categoryGroups]);
  const multValues = useMemo(() => multipliers.reduce((a, m) => ({ ...a, [m.id]: m.value }), {}), [multipliers]);

  useEffect(() => {
    const points = catPoints[form.category];
    if (!points || !form.size) { setCalc(null); return; }
    const base = points[form.size];
    const mults = form.multipliers.slice(0, 2);
    const mv = Math.min(mults.reduce((a, m) => a * (multValues[m] || 1), 1), 3);
    const total = Math.round(base * mv);
    setCalc({ base, total, immediate: Math.round(total * 0.5), deferred: total - Math.round(total * 0.5), multiplierValue: mv });
  }, [form.category, form.size, form.multipliers, catPoints, multValues]);

  const toggleMult = (id) => {
    setForm(p => {
      const has = p.multipliers.includes(id);
      if (has) return { ...p, multipliers: p.multipliers.filter(m => m !== id) };
      if (p.multipliers.length >= 2) return p;
      return { ...p, multipliers: [...p.multipliers, id] };
    });
  };

  const closeForm = () => {
    setShowForm(false);
    setLinkedQuest(null);
    setForm(blankForm);
    if (searchParams.get('quest')) setSearchParams({}, { replace: true });
  };

  const submit = async () => {
    try {
      const { data } = await api.post('/contributions', form);
      setContribs(p => [data, ...p]);
      closeForm();
      setGithubResult(data.github);
      showToast(`Submitted! Awaiting ${2} peer reviews → SME → CWG. Points awarded after CWG approval.`, 'success');
      loadQueues();
    } catch (e) { showToast(e.response?.data?.error || 'Failed to submit', 'error'); }
  };

  const openReview = (c) => {
    const existing = (c.peerReviews || []).find(r => r.reviewerId === user?.id);
    setReviewTarget(c);
    setReviewForm(existing
      ? { ratings: { ...BLANK_PEER_RATINGS, ...existing.ratings }, flags: existing.flags || [], suggestedCategory: existing.suggestedCategory || '', comment: existing.comment || '' }
      : { ratings: { ...BLANK_PEER_RATINGS }, flags: [], suggestedCategory: '', comment: '' });
  };

  const submitPeerReview = async () => {
    const rated = Object.values(reviewForm.ratings).some(v => v > 0);
    if (!rated) return showToast('Rate at least one dimension', 'error');
    try {
      const { data } = await api.post(`/contributions/${reviewTarget.id}/peer-review`, reviewForm);
      const msg = data.updated ? 'Review updated!' : `Review submitted — +${data.awarded} pts earned!`;
      showToast(msg, 'success');
      setReviewTarget(null);
      setReviewForm({ ratings: { ...BLANK_PEER_RATINGS }, flags: [], suggestedCategory: '', comment: '' });
      setContribs(p => p.map(c => c.id === reviewTarget.id ? data.contribution : c));
      loadQueues();
    } catch (e) { showToast(e.response?.data?.error || 'Failed to submit review', 'error'); }
  };

  const submitSmeReview = async () => {
    try {
      // Compute smeScore from star ratings if provided, otherwise use manual slider
      const ratedVals = Object.values(smeForm.smeRatings || {}).filter(v => v > 0);
      const computedScore = ratedVals.length > 0
        ? Math.round(ratedVals.reduce((a, b) => a + b, 0) / ratedVals.length * 20)
        : smeForm.smeScore;
      const payload = { ...smeForm, smeScore: computedScore };
      const { data } = await api.patch(`/contributions/${smeTarget.id}/sme-review`, payload);
      showToast(`SME review submitted — ${smeForm.decision === 'approve' ? `score ${computedScore}/100 → CWG queue` : 'rejected'}.`, smeForm.decision === 'approve' ? 'success' : 'error');
      setSmeTarget(null);
      setSmeForm({ smeScore: 85, notes: '', decision: 'approve', smeRatings: { ...BLANK_SME_RATINGS } });
      setContribs(p => p.map(c => c.id === data.id ? data : c));
      loadQueues();
    } catch (e) { showToast(e.response?.data?.error || 'SME review failed', 'error'); }
  };

  const submitCwgApproval = async () => {
    try {
      const { data } = await api.post(`/contributions/${cwgTarget.id}/approve`, cwgForm);
      showToast(`✅ Approved! ${data.immediatePoints} pts credited to ${data.contributorName}.`, 'success');
      setCwgTarget(null);
      setCwgForm({ finalPoints: '', notes: '' });
      setContribs(p => p.map(c => c.id === data.id ? data : c));
      loadQueues();
    } catch (e) { showToast(e.response?.data?.error || 'Approval failed', 'error'); }
  };

  const submitReject = async () => {
    try {
      const { data } = await api.post(`/contributions/${rejectTarget.id}/reject`, rejectForm);
      showToast('Contribution rejected.', 'error');
      setRejectTarget(null);
      setRejectForm({ reason: '' });
      setContribs(p => p.map(c => c.id === data.id ? data : c));
      loadQueues();
    } catch (e) { showToast(e.response?.data?.error || 'Rejection failed', 'error'); }
  };

  const submitAdoption = async () => {
    try {
      const { data } = await api.post(`/contributions/${adoptTarget.id}/adopt`, {});
      showToast(`🎉 Adoption confirmed! ${data.deferredPoints} pts released to ${data.contributorName}.`, 'success');
      setAdoptTarget(null);
      setContribs(p => p.map(c => c.id === data.id ? data : c));
      loadQueues();
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const showToast = (msg, type) => { setToast({ msg, type }); setTimeout(() => setToast(null), 4500); };

  const ReviewPill = ({ c }) => (
    <span className={`pill pill-${c.status}`} style={{ fontSize: 9, padding: '1px 5px' }}>
      {c.status === 'submitted' ? 'PEER REVIEW' : c.status === 'peer_reviewed' ? 'SME REVIEW' : c.status === 'sme_approved' ? 'CWG REVIEW' : c.status?.toUpperCase?.()}
    </span>
  );

  return (
    <div>
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      {/* GitHub success banner */}
      {githubResult && (
        <div style={{ background: githubResult.status === 'invited' ? 'rgba(0,255,136,0.07)' : 'rgba(255,215,0,0.07)', border: `1px solid ${githubResult.status === 'invited' ? 'var(--j5)' : 'var(--j4)'}`, borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {githubResult.status === 'invited' && <div style={{ color: 'var(--j5)', fontWeight: 700, marginBottom: 4 }}>✓ GitHub Invite Sent</div>}
              {githubResult.status === 'already_member' && <div style={{ color: 'var(--j5)', fontWeight: 700, marginBottom: 4 }}>✓ Reviewer already has access</div>}
              {(githubResult.status === 'manual' || githubResult.status === 'failed' || githubResult.status === 'invalid_url') && (
                <div style={{ color: 'var(--j4)', fontWeight: 700, marginBottom: 4 }}>
                  ⚠ Manual GitHub Access Required
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--txt2)', lineHeight: 1.6 }}>
                {githubResult.status === 'invited' && `An invitation has been sent to @${githubResult.reviewerUsername} for read access to your repo.`}
                {githubResult.status === 'manual' && <>Go to your repo Settings → Collaborators → Add <strong>{githubResult.reviewerUsername || 'rahul.pandey-wissen'}</strong> (or search email <strong>rahul.pandey@wissen.com</strong>) with Read access.</>}
                {githubResult.status === 'failed' && <>Auto-invite failed. Please manually add <strong>rahul.pandey@wissen.com</strong> as a collaborator with Read access on your GitHub repo.</>}
                {githubResult.status === 'invalid_url' && <>The GitHub URL was not recognised. Please add <strong>rahul.pandey@wissen.com</strong> manually as a collaborator on your repo.</>}
              </div>
            </div>
            <button onClick={() => setGithubResult(null)} style={{ background: 'none', border: 'none', color: 'var(--txt3)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div><div className="page-title">CONTRIBUTIONS</div><div className="page-sub">Submit → Peer Review → SME → CWG → Bounty</div></div>
        {can('member', 'sme', 'cwg', 'stakeholder') && <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ SUBMIT CONTRIBUTION</button>}
      </div>

      {/* Points flow info */}
      <div className="card" style={{ marginBottom: 20, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {[{l:'On CWG Approval',v:'50%'},{l:'60-Day Adoption Gate',v:'+50%'},{l:'Peer Review Reward',v:'+50 pts'},{l:'Weekly Cap',v:'1,500 pts'},{l:'Max Multipliers',v:'2 stacked, ≤3×'}].map(i => (
          <div key={i.l}><div style={{ fontFamily: 'Orbitron', fontSize: 18, fontWeight: 800, color: 'var(--j1)' }}>{i.v}</div><div className="stat-label">{i.l}</div></div>
        ))}
      </div>

      {/* Peer Review Queue — visible to all reviewer-eligible roles */}
      {can('member', 'sme', 'cwg', 'stakeholder') && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid rgba(0,212,255,0.3)' }}>
          <div className="section-header">
            <div className="section-title" style={{ color: 'var(--j1)' }}>👥 PEER REVIEW QUEUE</div>
            <span style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>Rate contributions from your peers · Earn +50 pts per review</span>
          </div>
          {peerQueue.length === 0 ? (
            <div style={{ padding: '16px 0', color: 'var(--txt3)', fontFamily: 'Share Tech Mono', fontSize: 11, textAlign: 'center' }}>
              No contributions awaiting your peer review right now.<br />
              <span style={{ fontSize: 10, color: 'var(--txt3)' }}>You can also click any row in the table below to open and review it.</span>
            </div>
          ) : peerQueue.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--brd2)', gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--txt)', fontSize: 13 }}>{c.title}</div>
                <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>by {c.contributorName} · {c.category} ({c.size}) · {c.peerReviewCount}/2 reviews</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {c.solutionLink && <a href={c.solutionLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--j3)', textDecoration: 'none' }}>▶ Demo</a>}
                  {c.githubRepo && <a href={c.githubRepo} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--txt3)', textDecoration: 'none' }}>⊙ Repo</a>}
                </div>
              </div>
              <button className="btn btn-sm" style={{ borderColor: 'var(--j1)', color: 'var(--j1)', flexShrink: 0 }} onClick={() => openReview(c)}>
                {(c.peerReviews || []).some(r => r.reviewerId === user?.id) ? 'EDIT REVIEW' : 'RATE & REVIEW'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* SME Queue */}
      {can('sme', 'cwg') && smeQueue.length > 0 && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid var(--j3)' }}>
          <div className="section-header"><div className="section-title" style={{ color: 'var(--j3)' }}>🔬 SME REVIEW QUEUE</div><span style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>Peer reviews complete — needs SME sign-off</span></div>
          {smeQueue.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--brd2)', gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--txt)', fontSize: 13 }}>{c.title}</div>
                <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>by {c.contributorName} · {c.category} ({c.size}) · Peer avg: {c.peerScore ?? '–'}/100</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {c.solutionLink && <a href={c.solutionLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--j3)', textDecoration: 'none' }}>▶ Demo / Video</a>}
                  {c.githubRepo && <a href={c.githubRepo} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--txt3)', textDecoration: 'none' }}>⊙ GitHub Repo</a>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm" style={{ borderColor: 'var(--j3)', color: 'var(--j3)', flexShrink: 0 }} onClick={() => { setSmeTarget(c); setSmeForm({ smeScore: 85, notes: '', decision: 'approve', smeRatings: { ...BLANK_SME_RATINGS } }); }}>SME REVIEW</button>
                <button className="btn btn-sm btn-danger" onClick={() => { setRejectTarget(c); setRejectForm({ reason: '' }); }}>REJECT</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CWG Queue */}
      {can('cwg', 'stakeholder') && cwgQueue.length > 0 && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid var(--j4)' }}>
          <div className="section-header"><div className="section-title" style={{ color: 'var(--j4)' }}>🏛 CWG APPROVAL QUEUE</div><span style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>SME approved — awaiting CWG final bounty decision</span></div>
          {cwgQueue.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--brd2)', gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--txt)', fontSize: 13 }}>{c.title}</div>
                <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>by {c.contributorName} · {c.category} ({c.size}) · Peer avg {c.peerScore ?? '–'}/100 · SME {c.smeScore ?? '–'}/100</div>
                <div style={{ fontSize: 11, color: 'var(--j4)', fontFamily: 'Orbitron', marginTop: 2 }}>{(c.totalPoints || 0).toLocaleString()} pts ({(c.immediatePoints || 0).toLocaleString()} now + {(c.deferredPoints || 0).toLocaleString()} @60d)</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {c.solutionLink && <a href={c.solutionLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--j3)', textDecoration: 'none' }}>▶ Demo / Video</a>}
                  {c.githubRepo && <a href={c.githubRepo} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--txt3)', textDecoration: 'none' }}>⊙ GitHub Repo</a>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm btn-success" onClick={() => { setCwgTarget(c); setCwgForm({ finalPoints: c.totalPoints, notes: '' }); }}>APPROVE & AWARD</button>
                <button className="btn btn-sm btn-danger" onClick={() => { setRejectTarget(c); setRejectForm({ reason: '' }); }}>REJECT</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Adoption Gate Queue */}
      {can('cwg') && adoptionQueue.length > 0 && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid var(--j5)' }}>
          <div className="section-header"><div className="section-title" style={{ color: 'var(--j5)' }}>⏰ ADOPTION GATE</div><span style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>60-day check passed — confirm adoption to release deferred points</span></div>
          {adoptionQueue.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--brd2)', gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--txt)', fontSize: 13 }}>{c.title}</div>
                <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>by {c.contributorName} · deferred {(c.deferredPoints || 0).toLocaleString()} pts · check due {new Date(c.adoptionCheckAt).toLocaleDateString()}</div>
              </div>
              <button className="btn btn-sm btn-success" onClick={() => setAdoptTarget(c)}>CONFIRM ADOPTION</button>
            </div>
          ))}
        </div>
      )}

      {/* Contributions table */}
      <PageTurn page={page}>
      <div className="card">
        <table className="table">
          <thead><tr><th>TITLE</th><th>CAT / SIZE</th><th>POINTS</th><th>PIPELINE</th><th>LINKS</th><th>DATE</th></tr></thead>
          <tbody>{contribs.slice(page * PAGE_TABLE, (page + 1) * PAGE_TABLE).map(c => (
            <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setDetailTarget(c)}>
              <td>
                <div style={{ fontWeight: 600, color: 'var(--txt)' }}>{c.title}</div>
                <div style={{ fontSize: 10, color: 'var(--txt2)' }}>{c.contributorName}{c.questTitle ? ` · ⚡ ${c.questTitle}` : ''}</div>
              </td>
              <td style={{ fontSize: 11 }}>
                <div style={{ color: 'var(--txt2)' }}>{c.category}</div>
                <span style={{ fontFamily: 'Share Tech Mono', color: c.size === 'L' ? 'var(--j3)' : c.size === 'M' ? 'var(--j4)' : 'var(--txt2)' }}>{c.size}</span>
              </td>
              <td>
                <div style={{ fontFamily: 'Orbitron', fontWeight: 700, color: 'var(--j1)', fontSize: 13 }}>{(c.totalPoints || 0).toLocaleString()}</div>
                <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>{c.pointsAwarded ? `${c.pointsAwarded} awarded` : 'pending'}</div>
              </td>
              <td onClick={e => e.stopPropagation()}>
                <PipelineMini status={c.status} peerCount={c.peerReviews?.length || 0} />
                <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginTop: 3 }}>Peers: {c.peerReviews?.length || 0}/2{c.peerScore != null ? ` · ${c.peerScore}` : ''}</div>
              </td>
              <td onClick={e => e.stopPropagation()} style={{ fontSize: 10 }}>
                {c.solutionLink && <div><a href={c.solutionLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--j3)', textDecoration: 'none' }}>▶ Demo</a></div>}
                {c.githubRepo && <div><a href={c.githubRepo} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--txt3)', textDecoration: 'none' }}>⊙ Repo</a></div>}
                {can('member', 'sme', 'cwg', 'stakeholder') && c.contributorId !== user?.id && (() => {
                  const alreadyReviewed = (c.peerReviews || []).some(r => r.reviewerId === user?.id);
                  const quotaOpen = (c.peerReviews || []).length < 2 && ['submitted', 'peer_reviewed'].includes(c.status);
                  if (!alreadyReviewed && !quotaOpen) return null;
                  return <button className="btn btn-sm" style={{ marginTop: 4, padding: '2px 6px', borderColor: 'var(--j1)', color: 'var(--j1)', fontSize: 9 }} onClick={() => openReview(c)}>{alreadyReviewed ? '✏ EDIT' : '⭐ RATE'}</button>;
                })()}
              </td>
              <td style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>{new Date(c.submittedAt).toLocaleDateString()}</td>
            </tr>
          ))}</tbody>
        </table>
        {contribs.length === 0 && <div className="empty-state">No contributions yet. Submit your first!</div>}
      </div>
      </PageTurn>
      <Paginate page={page} total={contribs.length} pageSize={PAGE_TABLE} onPage={setPage} />

      {/* ── Contribution Detail Modal ── */}
      {detailTarget && (
        <div className="modal-overlay" onClick={() => setDetailTarget(null)}>
          <div className="modal" style={{ maxWidth: 640, width: '92%' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setDetailTarget(null)}>×</button>
            <div className="modal-title">{detailTarget.title}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <ReviewPill c={detailTarget} />
              <span style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{detailTarget.category} · {detailTarget.size} · by {detailTarget.contributorName}</span>
            </div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
              {[{l:'Total',v:`${(detailTarget.totalPoints||0).toLocaleString()} pts`},{l:'50% Now',v:`${(detailTarget.immediatePoints||0).toLocaleString()}`},{l:'50% @60d',v:`${(detailTarget.deferredPoints||0).toLocaleString()}`},{l:'Awarded',v:`${(detailTarget.pointsAwarded||0).toLocaleString()}`}].map(s => (
                <div key={s.l}><div style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 800, color: 'var(--j1)' }}>{s.v}</div><div className="stat-label">{s.l}</div></div>
              ))}
            </div>
            {/* Pipeline */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginBottom: 6, letterSpacing: 1 }}>REVIEW PIPELINE</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {PIPELINE_STAGES.map((s, i) => {
                  const idx = pipelineStageIdx(detailTarget.status);
                  const stageNum = i + 1;
                  const done = idx > stageNum && detailTarget.status !== 'rejected';
                  const active = idx === stageNum;
                  const rejected = detailTarget.status === 'rejected' && active;
                  return (
                    <React.Fragment key={s.key}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: rejected ? 'rgba(255,107,53,0.2)' : done ? 'rgba(0,255,136,0.2)' : active ? 'rgba(0,212,255,0.2)' : 'var(--bg3)', border: `2px solid ${rejected ? 'var(--j6)' : done ? 'var(--j5)' : active ? 'var(--j1)' : 'var(--brd)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                          {done ? '✓' : rejected ? '✕' : i + 1}
                        </div>
                        <div style={{ fontSize: 8, color: done || active ? 'var(--txt)' : 'var(--txt3)', marginTop: 4, textAlign: 'center', fontFamily: 'Share Tech Mono', letterSpacing: 0.5 }}>{s.label.toUpperCase()}</div>
                      </div>
                      {i < PIPELINE_STAGES.length - 1 && <div style={{ flex: 1, height: 2, background: done ? 'var(--j5)' : 'var(--brd)', margin: '-10px 0 0', maxWidth: 40 }} />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            {/* Links + review action */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              {detailTarget.solutionLink && <a href={detailTarget.solutionLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: 'var(--j3)', color: 'var(--j3)' }}>▶ Demo / Video</a>}
              {detailTarget.githubRepo && <a href={detailTarget.githubRepo} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }}>⊙ GitHub Repo</a>}
              {/* Rate / edit review button — eligible if not own contribution */}
              {can('member', 'sme', 'cwg', 'stakeholder') && detailTarget.contributorId !== user?.id && (() => {
                const alreadyReviewed = (detailTarget.peerReviews || []).some(r => r.reviewerId === user?.id);
                const quotaOpen = (detailTarget.peerReviews || []).length < 2 && ['submitted', 'peer_reviewed'].includes(detailTarget.status);
                if (!alreadyReviewed && !quotaOpen) return null;
                return (
                  <button className="btn btn-sm" style={{ borderColor: 'var(--j1)', color: 'var(--j1)', marginLeft: 'auto' }} onClick={() => { setDetailTarget(null); openReview(detailTarget); }}>
                    {alreadyReviewed ? '✏ Edit My Review' : '⭐ Rate & Review'}
                  </button>
                );
              })()}
            </div>
            {detailTarget.description && <div style={{ fontSize: 12, color: 'var(--txt2)', lineHeight: 1.6, marginBottom: 10 }}><strong style={{ color: 'var(--txt)', display: 'block', marginBottom: 4 }}>Description</strong>{detailTarget.description}</div>}
            {detailTarget.evidence && <div style={{ fontSize: 12, color: 'var(--txt2)', lineHeight: 1.6, marginBottom: 14 }}><strong style={{ color: 'var(--txt)', display: 'block', marginBottom: 4 }}>Evidence of Impact</strong>{detailTarget.evidence}</div>}
            {/* Peer reviews */}
            {(detailTarget.peerReviews || []).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 8 }}>PEER REVIEWS ({detailTarget.peerReviews.length}/2)</div>
                {/* Flags summary */}
                {detailTarget.flagsSummary && Object.keys(detailTarget.flagsSummary).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                    {Object.entries(detailTarget.flagsSummary).map(([k, n]) => (
                      <span key={k} style={{ fontSize: 10, padding: '2px 7px', background: 'rgba(255,107,53,0.12)', border: '1px solid rgba(255,107,53,0.4)', borderRadius: 10, color: 'var(--j6)' }}>
                        {FLAG_LABELS[k] || k}{n > 1 ? ` ×${n}` : ''}
                      </span>
                    ))}
                  </div>
                )}
                {detailTarget.peerReviews.map((r, i) => {
                  const isOwn = r.reviewerId === user?.id;
                  const hasRatings = r.ratings && Object.values(r.ratings).some(v => v > 0);
                  return (
                    <div key={r.id || i} style={{ background: isOwn ? 'rgba(0,212,255,0.04)' : 'rgba(255,255,255,0.03)', border: `1px solid ${isOwn ? 'rgba(0,212,255,0.25)' : 'var(--brd2)'}`, borderRadius: 4, padding: '10px 12px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{r.reviewerName}{isOwn && ' (you)'}</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          {isOwn && can('member','sme') && detailTarget.status === 'submitted' && (
                            <button className="btn btn-sm" style={{ padding: '2px 8px', fontSize: 10, borderColor: 'var(--j1)', color: 'var(--j1)' }} onClick={() => {
                              setDetailTarget(null);
                              setReviewTarget(detailTarget);
                              setReviewForm({ ratings: { ...BLANK_PEER_RATINGS, ...r.ratings }, flags: r.flags || [], suggestedCategory: r.suggestedCategory || '', comment: r.comment || '' });
                            }}>EDIT</button>
                          )}
                          <span style={{ fontFamily: 'Orbitron', fontSize: 13, color: r.score >= 70 ? 'var(--j5)' : r.score >= 50 ? 'var(--j4)' : 'var(--j6)' }}>{r.score}/100</span>
                        </div>
                      </div>
                      {/* Per-dimension stars */}
                      {hasRatings && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: 6 }}>
                          {PEER_PARAMS.filter(p => r.ratings[p.key] > 0).map(p => (
                            <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 11 }}>{p.icon}</span>
                              <span style={{ fontSize: 10, color: 'var(--txt2)', minWidth: 100 }}>{p.label}</span>
                              <StarRating value={r.ratings[p.key]} readonly size={12} />
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Flags */}
                      {(r.flags || []).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 5 }}>
                          {r.flags.map(f => <span key={f} style={{ fontSize: 9, padding: '1px 6px', background: 'rgba(255,107,53,0.1)', border: '1px solid rgba(255,107,53,0.3)', borderRadius: 8, color: 'var(--j6)' }}>{FLAG_LABELS[f] || f}</span>)}
                        </div>
                      )}
                      {r.suggestedCategory && <div style={{ fontSize: 10, color: 'var(--j4)', marginBottom: 4 }}>Suggested category: {r.suggestedCategory}</div>}
                      {r.comment && <div style={{ fontSize: 11, color: 'var(--txt2)', fontStyle: 'italic' }}>{r.comment}</div>}
                    </div>
                  );
                })}
              </div>
            )}
            {/* SME review */}
            {detailTarget.smeScore != null && (
              <div style={{ background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 4, padding: '8px 12px', marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontSize: 11, color: 'var(--j3)', fontFamily: 'Share Tech Mono' }}>SME REVIEW — {detailTarget.smeReviewerName || 'SME'}</span>
                  <span style={{ fontFamily: 'Orbitron', fontSize: 13, color: 'var(--j3)' }}>{detailTarget.smeScore}/100</span>
                </div>
                {detailTarget.smeNotes && <div style={{ fontSize: 11, color: 'var(--txt2)', marginTop: 2 }}>{detailTarget.smeNotes}</div>}
              </div>
            )}
            {/* CWG notes */}
            {detailTarget.cwgNotes && (
              <div style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.3)', borderRadius: 4, padding: '8px 12px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--j4)', fontFamily: 'Share Tech Mono', marginBottom: 2 }}>CWG NOTES — {detailTarget.approverName || 'CWG'}</div>
                <div style={{ fontSize: 11, color: 'var(--txt2)' }}>{detailTarget.cwgNotes}</div>
              </div>
            )}
            {detailTarget.rejectionReason && (
              <div style={{ background: 'rgba(255,107,53,0.07)', border: '1px solid var(--j6)', borderRadius: 4, padding: '8px 12px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--j6)', marginBottom: 2, fontFamily: 'Share Tech Mono' }}>REJECTION REASON</div>
                <div style={{ fontSize: 11, color: 'var(--txt2)' }}>{detailTarget.rejectionReason}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Submit form ── */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 620 }}>
            <button className="modal-close" onClick={closeForm}>×</button>
            <div className="modal-title">SUBMIT CONTRIBUTION</div>
            {linkedQuest && (
              <div style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid var(--j5)', borderRadius: 3, padding: '10px 12px', marginBottom: 16, fontSize: 12 }}>
                <span style={{ color: 'var(--j5)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>⚡ QUEST SUBMISSION</span>
                <div style={{ color: 'var(--txt)', marginTop: 4, fontWeight: 600 }}>{linkedQuest.title}</div>
                <div style={{ color: 'var(--txt2)', fontSize: 10, marginTop: 2 }}>Bounty {linkedQuest.bountyMin?.toLocaleString()}–{linkedQuest.bountyMax?.toLocaleString()} pts</div>
              </div>
            )}
            <div className="form-group"><label className="label">Title</label><input className="input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Descriptive title for your contribution" /></div>
            <div className="form-group"><label className="label">Category <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>· auto-prices your contribution</span></label>
              <CategorySelect value={form.category} groups={categoryGroups} onChange={v => setForm(p => ({ ...p, category: v }))} />
            </div>
            <div className="form-group"><label className="label">Size Estimate <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>(SME may override)</span></label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['S','M','L'].map(s => <button key={s} onClick={() => setForm(p => ({ ...p, size: s }))} className="btn" style={{ flex: 1, borderColor: form.size === s ? 'var(--j1)' : 'var(--brd2)', color: form.size === s ? 'var(--j1)' : 'var(--txt2)', background: form.size === s ? 'rgba(0,212,255,0.08)' : 'transparent' }}>{s}</button>)}
              </div>
            </div>
            <div className="form-group"><label className="label">Description</label><textarea className="input" rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="What did you build? How does it work? What problem does it solve?" /></div>
            <div className="form-group"><label className="label">Evidence of Impact</label><textarea className="input" rows={2} value={form.evidence} onChange={e => setForm(p => ({ ...p, evidence: e.target.value }))} placeholder="Metrics, test results, adoption evidence…" /></div>

            {/* GitHub Repo — required */}
            <div className="form-group">
              <label className="label">GitHub Repository URL <span style={{ color: 'var(--j6)' }}>*</span></label>
              <input className="input" value={form.githubRepo} onChange={e => setForm(p => ({ ...p, githubRepo: e.target.value }))} placeholder="https://github.com/your-username/my-project" />
              <div style={{ fontSize: 10, color: 'var(--txt3)', marginTop: 4, lineHeight: 1.5 }}>
                The reviewer (<strong style={{ color: 'var(--j1)' }}>rahul.pandey@wissen.com</strong>) will automatically receive a GitHub read-access invite. Ensure your repo is accessible (not under a locked org policy).
              </div>
            </div>

            {/* Solution / Demo link — required */}
            <div className="form-group">
              <label className="label">Demo / Solution Link <span style={{ color: 'var(--j6)' }}>*</span></label>
              <input className="input" value={form.solutionLink} onChange={e => setForm(p => ({ ...p, solutionLink: e.target.value }))} placeholder="https://loom.com/share/… · deployed app URL · slide deck" />
              <div style={{ fontSize: 10, color: 'var(--txt3)', marginTop: 4 }}>
                A Loom video walkthrough, live deployment, or presentation is mandatory for review.
              </div>
            </div>

            <div className="form-group">
              <label className="label">Multipliers <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>(max 2, combined cap 3×)</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {multipliers.map(m => (
                  <button key={m.id} onClick={() => toggleMult(m.id)} className="btn btn-sm" style={{ borderColor: form.multipliers.includes(m.id) ? 'var(--j4)' : 'var(--brd2)', color: form.multipliers.includes(m.id) ? 'var(--j4)' : 'var(--txt2)', background: form.multipliers.includes(m.id) ? 'rgba(255,215,0,0.08)' : 'transparent' }}>×{m.value} {m.label}</button>
                ))}
              </div>
            </div>
            {calc && (
              <div style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid var(--brd)', borderRadius: 3, padding: 14, marginBottom: 16 }}>
                <div style={{ fontFamily: 'Share Tech Mono', fontSize: 10, color: 'var(--txt2)', marginBottom: 8, letterSpacing: 1 }}>ESTIMATED BOUNTY — AWARDED AFTER CWG APPROVAL</div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div><div style={{ fontFamily: 'Orbitron', fontSize: 14, color: 'var(--txt2)' }}>{calc.base}</div><div className="stat-label">Base</div></div>
                  <div><div style={{ fontFamily: 'Orbitron', fontSize: 14, color: 'var(--j4)' }}>×{calc.multiplierValue.toFixed(2)}</div><div className="stat-label">Multiplier</div></div>
                  <div><div style={{ fontFamily: 'Orbitron', fontSize: 20, fontWeight: 800, color: 'var(--j1)' }}>{calc.total}</div><div className="stat-label">Total</div></div>
                  <div><div style={{ fontFamily: 'Orbitron', fontSize: 14, color: 'var(--j5)' }}>{calc.immediate}</div><div className="stat-label">On Approval</div></div>
                  <div><div style={{ fontFamily: 'Orbitron', fontSize: 14, color: 'var(--txt2)' }}>{calc.deferred}</div><div className="stat-label">+60-Day Gate</div></div>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={closeForm}>CANCEL</button>
              <button className="btn btn-primary" onClick={submit} disabled={!form.title || !form.category || !form.githubRepo || !form.solutionLink}>SUBMIT FOR REVIEW</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Peer Review modal ── */}
      {reviewTarget && (() => {
        const isEdit = (reviewTarget.peerReviews || []).some(r => r.reviewerId === user?.id);
        const ratedCount = Object.values(reviewForm.ratings).filter(v => v > 0).length;
        const autoScore = ratedCount > 0 ? Math.round(Object.values(reviewForm.ratings).filter(v => v > 0).reduce((a, b) => a + b, 0) / ratedCount * 20) : 0;
        return (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 560 }}>
              <button className="modal-close" onClick={() => setReviewTarget(null)}>×</button>
              <div className="modal-title">{isEdit ? 'UPDATE PEER REVIEW' : 'PEER REVIEW'}</div>

              {/* Contribution info */}
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--brd2)', borderRadius: 4, padding: '10px 12px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: 'var(--txt)', marginBottom: 2 }}>{reviewTarget.title}</div>
                <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', marginBottom: 8 }}>by {reviewTarget.contributorName} · <span style={{ color: 'var(--j1)' }}>{reviewTarget.category}</span> · Size {reviewTarget.size}</div>
                {reviewTarget.description && <div style={{ fontSize: 11, color: 'var(--txt2)', lineHeight: 1.5, marginBottom: 8 }}>{reviewTarget.description}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  {reviewTarget.solutionLink && <a href={reviewTarget.solutionLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: 'var(--j3)', color: 'var(--j3)' }}>▶ Demo / Video</a>}
                  {reviewTarget.githubRepo && <a href={reviewTarget.githubRepo} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }}>⊙ GitHub Repo</a>}
                </div>
              </div>

              {/* Star rating grid */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span className="label" style={{ margin: 0 }}>RATE EACH DIMENSION</span>
                  {ratedCount > 0 && <span style={{ fontFamily: 'Orbitron', fontSize: 18, fontWeight: 800, color: autoScore >= 70 ? 'var(--j5)' : autoScore >= 50 ? 'var(--j4)' : 'var(--j6)' }}>{autoScore}<span style={{ fontSize: 10, color: 'var(--txt3)' }}>/100</span></span>}
                </div>
                {PEER_PARAMS.map(p => (
                  <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--brd2)' }}>
                    <div style={{ width: 24, textAlign: 'center', fontSize: 15 }}>{p.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt)' }}>{p.label}</div>
                      <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginTop: 1 }}>{p.desc}</div>
                    </div>
                    <StarRating value={reviewForm.ratings[p.key]} onChange={v => setReviewForm(prev => ({ ...prev, ratings: { ...prev.ratings, [p.key]: v } }))} size={22} />
                    {reviewForm.ratings[p.key] > 0 && (
                      <span style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', width: 28, textAlign: 'right' }}>{reviewForm.ratings[p.key] * 20}/100</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Issue flags */}
              <div style={{ marginBottom: 14 }}>
                <div className="label" style={{ marginBottom: 8 }}>FLAG ISSUES <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>(select all that apply)</span></div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {PEER_FLAGS.map(f => {
                    const on = reviewForm.flags.includes(f.key);
                    return (
                      <button key={f.key} onClick={() => setReviewForm(prev => ({ ...prev, flags: on ? prev.flags.filter(x => x !== f.key) : [...prev.flags, f.key] }))} className="btn btn-sm" style={{ borderColor: on ? 'var(--j6)' : 'var(--brd2)', color: on ? 'var(--j6)' : 'var(--txt2)', background: on ? 'rgba(255,107,53,0.1)' : 'transparent', fontSize: 10 }}>
                        {f.icon} {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category correction */}
              {reviewForm.flags.includes('wrong_category') && (
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="label">Suggested Correct Category</label>
                  <input className="input" value={reviewForm.suggestedCategory} onChange={e => setReviewForm(p => ({ ...p, suggestedCategory: e.target.value }))} placeholder="e.g. Automation · Data Engineering · Platform" />
                </div>
              )}

              {/* Comment */}
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="label">Written Feedback <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>(optional — shown to contributor)</span></label>
                <textarea className="input" rows={3} value={reviewForm.comment} onChange={e => setReviewForm(p => ({ ...p, comment: e.target.value }))} maxLength={500} placeholder="What's strong? What's broken? What would make this better?" />
                <div style={{ fontSize: 9, color: 'var(--txt3)', textAlign: 'right', fontFamily: 'Share Tech Mono' }}>{reviewForm.comment.length}/500</div>
              </div>

              {!isEdit && <div style={{ fontSize: 11, color: 'var(--j5)', fontFamily: 'Share Tech Mono', marginBottom: 12 }}>+50 pts credited to you for completing this peer review.</div>}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setReviewTarget(null)}>CANCEL</button>
                <button className="btn btn-primary" onClick={submitPeerReview} disabled={ratedCount === 0}>{isEdit ? 'UPDATE REVIEW' : 'SUBMIT REVIEW'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── SME Review modal ── */}
      {smeTarget && (() => {
        const smeRatedCount = Object.values(smeForm.smeRatings || {}).filter(v => v > 0).length;
        const smeAutoScore = smeRatedCount > 0
          ? Math.round(Object.values(smeForm.smeRatings || {}).filter(v => v > 0).reduce((a, b) => a + b, 0) / smeRatedCount * 20)
          : smeForm.smeScore;
        return (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 560 }}>
              <button className="modal-close" onClick={() => setSmeTarget(null)}>×</button>
              <div className="modal-title">SME REVIEW</div>

              {/* Context */}
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--brd2)', borderRadius: 4, padding: '10px 12px', marginBottom: 14 }}>
                <div style={{ fontWeight: 700, color: 'var(--txt)', marginBottom: 2 }}>{smeTarget.title}</div>
                <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', marginBottom: 8 }}>by {smeTarget.contributorName} · {smeTarget.category} ({smeTarget.size}) · Peer avg {smeTarget.peerScore ?? '–'}/100</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {smeTarget.solutionLink && <a href={smeTarget.solutionLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: 'var(--j3)', color: 'var(--j3)' }}>▶ Demo / Video</a>}
                  {smeTarget.githubRepo && <a href={smeTarget.githubRepo} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }}>⊙ GitHub Repo</a>}
                </div>
                {/* Peer flags summary */}
                {smeTarget.flagsSummary && Object.keys(smeTarget.flagsSummary).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 9, color: 'var(--j6)', fontFamily: 'Share Tech Mono', marginBottom: 4 }}>PEER FLAGS</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {Object.entries(smeTarget.flagsSummary).map(([k, n]) => (
                        <span key={k} style={{ fontSize: 10, padding: '1px 7px', background: 'rgba(255,107,53,0.1)', border: '1px solid rgba(255,107,53,0.35)', borderRadius: 8, color: 'var(--j6)' }}>{FLAG_LABELS[k] || k}{n > 1 ? ` ×${n}` : ''}</span>
                      ))}
                    </div>
                  </div>
                )}
                {smeTarget.description && <div style={{ fontSize: 11, color: 'var(--txt2)', lineHeight: 1.5, marginTop: 8 }}>{smeTarget.description}</div>}
              </div>

              {/* SME star ratings */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span className="label" style={{ margin: 0 }}>TECHNICAL ASSESSMENT</span>
                  {smeRatedCount > 0 && <span style={{ fontFamily: 'Orbitron', fontSize: 18, fontWeight: 800, color: smeAutoScore >= 70 ? '#a855f7' : smeAutoScore >= 50 ? 'var(--j4)' : 'var(--j6)' }}>{smeAutoScore}<span style={{ fontSize: 10, color: 'var(--txt3)' }}>/100</span></span>}
                </div>
                {SME_PARAMS.map(p => (
                  <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--brd2)' }}>
                    <div style={{ width: 24, textAlign: 'center', fontSize: 15 }}>{p.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt)' }}>{p.label}</div>
                      <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginTop: 1 }}>{p.desc}</div>
                    </div>
                    <StarRating value={(smeForm.smeRatings || {})[p.key] || 0} onChange={v => setSmeForm(prev => ({ ...prev, smeRatings: { ...(prev.smeRatings || {}), [p.key]: v } }))} size={22} />
                  </div>
                ))}
                {smeRatedCount === 0 && (
                  <div style={{ marginTop: 8 }}>
                    <label className="label">Manual Score <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>(if not using stars above)</span></label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="range" min="0" max="100" value={smeForm.smeScore} onChange={e => setSmeForm(p => ({ ...p, smeScore: +e.target.value }))} style={{ flex: 1, accentColor: '#a855f7' }} />
                      <span style={{ fontFamily: 'Orbitron', color: '#a855f7', width: 50, textAlign: 'right' }}>{smeForm.smeScore}/100</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Size override */}
              <div className="form-group">
                <label className="label">Override Size <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>(optional — recalculates bounty)</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['S','M','L'].map(s => (
                    <button key={s} onClick={() => setSmeForm(p => ({ ...p, overrideSize: p.overrideSize === s ? undefined : s }))} className="btn" style={{ flex: 1, borderColor: smeForm.overrideSize === s ? '#a855f7' : 'var(--brd2)', color: smeForm.overrideSize === s ? '#a855f7' : 'var(--txt2)', background: smeForm.overrideSize === s ? 'rgba(168,85,247,0.1)' : 'transparent', fontSize: 12 }}>
                      {s}{s === smeTarget.size ? ' ◀ current' : ''}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="label">Technical Feedback <span style={{ color: 'var(--txt3)' }}>(shown to contributor)</span></label>
                <textarea className="input" rows={3} value={smeForm.notes} onChange={e => setSmeForm(p => ({ ...p, notes: e.target.value }))} placeholder="What's technically strong? What's missing? Specific code quality or architecture concerns?" />
              </div>

              {/* Decision */}
              <div className="form-group">
                <label className="label">Decision</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{v:'approve',l:'✓ Approve → CWG Queue'},{v:'reject',l:'✕ Reject'}].map(d => (
                    <button key={d.v} onClick={() => setSmeForm(p => ({ ...p, decision: d.v }))} className="btn" style={{ flex: 1, borderColor: smeForm.decision === d.v ? (d.v === 'approve' ? 'var(--j5)' : 'var(--j6)') : 'var(--brd2)', color: smeForm.decision === d.v ? (d.v === 'approve' ? 'var(--j5)' : 'var(--j6)') : 'var(--txt2)', background: smeForm.decision === d.v ? (d.v === 'approve' ? 'rgba(0,255,136,0.08)' : 'rgba(255,107,53,0.08)') : 'transparent' }}>{d.l}</button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setSmeTarget(null)}>CANCEL</button>
                <button className={`btn ${smeForm.decision === 'approve' ? 'btn-success' : 'btn-danger'}`} onClick={submitSmeReview}>{smeForm.decision === 'approve' ? 'APPROVE & ADVANCE' : 'REJECT'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── CWG Approval modal ── */}
      {cwgTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <button className="modal-close" onClick={() => setCwgTarget(null)}>×</button>
            <div className="modal-title">CWG FINAL APPROVAL</div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: 'var(--txt)', marginBottom: 4 }}>{cwgTarget.title}</div>
              <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>by {cwgTarget.contributorName} · Peer avg {cwgTarget.peerScore ?? '–'}/100 · SME {cwgTarget.smeScore ?? '–'}/100</div>
              {cwgTarget.smeNotes && <div style={{ fontSize: 11, color: 'var(--j3)', marginTop: 6 }}>SME: {cwgTarget.smeNotes}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                {cwgTarget.solutionLink && <a href={cwgTarget.solutionLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: 'var(--j3)', color: 'var(--j3)' }}>▶ Demo</a>}
                {cwgTarget.githubRepo && <a href={cwgTarget.githubRepo} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }}>⊙ Repo</a>}
              </div>
            </div>
            <div className="form-group">
              <label className="label">Final Bounty Points <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>(override calculated amount if needed)</span></label>
              <input type="number" className="input" value={cwgForm.finalPoints} onChange={e => setCwgForm(p => ({ ...p, finalPoints: e.target.value }))} />
              <div style={{ fontSize: 10, color: 'var(--txt3)', marginTop: 4 }}>50% credited immediately on approval · 50% at 60-day adoption gate</div>
            </div>
            <div className="form-group"><label className="label">CWG Notes <span style={{ color: 'var(--txt3)' }}>(shown to contributor)</span></label>
              <textarea className="input" rows={2} value={cwgForm.notes} onChange={e => setCwgForm(p => ({ ...p, notes: e.target.value }))} placeholder="Programme recognition, context, or any conditions…" /></div>
            <div style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid var(--j5)', borderRadius: 4, padding: '10px 14px', marginBottom: 14, fontSize: 12 }}>
              <strong style={{ color: 'var(--j5)' }}>Points on Approval:</strong>{' '}
              <span style={{ fontFamily: 'Orbitron', color: 'var(--j1)' }}>{Math.round((cwgForm.finalPoints || cwgTarget.totalPoints) * 0.5).toLocaleString()} pts</span> credited immediately to {cwgTarget.contributorName}.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setCwgTarget(null)}>CANCEL</button>
              <button className="btn btn-success" onClick={submitCwgApproval}>✓ APPROVE & AWARD BOUNTY</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject modal ── */}
      {rejectTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <button className="modal-close" onClick={() => setRejectTarget(null)}>×</button>
            <div className="modal-title">REJECT CONTRIBUTION</div>
            <div style={{ fontWeight: 600, color: 'var(--txt)', marginBottom: 4 }}>{rejectTarget.title}</div>
            <div style={{ fontSize: 11, color: 'var(--txt2)', marginBottom: 14 }}>by {rejectTarget.contributorName}</div>
            <div className="form-group"><label className="label">Rejection Reason <span style={{ color: 'var(--txt3)' }}>(sent to contributor)</span></label>
              <textarea className="input" rows={3} value={rejectForm.reason} onChange={e => setRejectForm(p => ({ ...p, reason: e.target.value }))} placeholder="Why is this being rejected? What could be improved for a resubmission?" /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setRejectTarget(null)}>CANCEL</button>
              <button className="btn btn-danger" onClick={submitReject}>REJECT</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Adoption Confirm modal ── */}
      {adoptTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <button className="modal-close" onClick={() => setAdoptTarget(null)}>×</button>
            <div className="modal-title">CONFIRM ADOPTION</div>
            <div style={{ fontWeight: 600, color: 'var(--txt)', marginBottom: 4 }}>{adoptTarget.title}</div>
            <div style={{ fontSize: 11, color: 'var(--txt2)', marginBottom: 16 }}>by {adoptTarget.contributorName}</div>
            <div style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid var(--j5)', borderRadius: 4, padding: '10px 14px', fontSize: 12, marginBottom: 16 }}>
              Confirming adoption will release <strong style={{ color: 'var(--j1)', fontFamily: 'Orbitron' }}>{(adoptTarget.deferredPoints || 0).toLocaleString()} pts</strong> to {adoptTarget.contributorName}.
              Total bounty: <strong style={{ color: 'var(--j4)', fontFamily: 'Orbitron' }}>{(adoptTarget.totalPoints || 0).toLocaleString()} pts</strong>.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setAdoptTarget(null)}>CANCEL</button>
              <button className="btn btn-success" onClick={submitAdoption}>✓ CONFIRM ADOPTION</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── USE CASE LIBRARY ────────────────────────────────────────────────────────
export function Library() {
  const { can } = useAuth();
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: '', tags: '', documentation: '' });
  const [reuseTarget, setReuseTarget] = useState(null);
  const [reuseTiers, setReuseTiers] = useState([]);
  const [reuseForm, setReuseForm] = useState({ reuseType: '', application: '', hoursSaved: '' });
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(0);
  const showToast = (msg, type) => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    api.get('/library').then(r => setEntries(r.data)).catch(() => {});
    api.get('/library/reuse-tiers').then(r => setReuseTiers(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    try {
      const { data } = await api.post('/library', { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) });
      setEntries(p => [data, ...p]); setShowForm(false);
      setForm({ title: '', description: '', category: '', tags: '', documentation: '' });
      showToast('Submitted for review!', 'success');
    } catch (e) { showToast(e.response?.data?.error || 'Failed to submit', 'error'); }
  };

  const submitReuse = async () => {
    if (!reuseForm.reuseType) return showToast('Select how you reused this', 'error');
    if (!reuseForm.application.trim()) return showToast('Describe how you applied it', 'error');
    try {
      const { data } = await api.post(`/library/${reuseTarget.id}/reuse`, reuseForm);
      setEntries(p => p.map(e => e.id === reuseTarget.id ? data.entry : e));
      setReuseTarget(null);
      setReuseForm({ reuseType: '', application: '', hoursSaved: '' });
      showToast(data.message, 'success');
    } catch (e) { showToast(e.response?.data?.error || 'Failed to register reuse', 'error'); }
  };

  const approve = async (id, status) => {
    try {
      const { data } = await api.patch(`/library/${id}/approve`, { status });
      setEntries(p => p.map(e => e.id === id ? data : e));
      showToast(`Entry ${status}!`, 'success');
    } catch { showToast('Failed', 'error'); }
  };

  return (
    <div>
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div><div className="page-title">USE CASE LIBRARY</div><div className="page-sub">Institutional memory · Reuse · Build faster</div></div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ SUBMIT ENTRY</button>
      </div>
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 24 }}>
        {[{l:'Total Entries',v:entries.filter(e=>e.status==='approved').length},{l:'Total Reuses',v:entries.reduce((s,e)=>s+(e.reuses||0),0)},{l:'Hours Saved',v:entries.reduce((s,e)=>s+(e.timeSavedHours||0),0)+'h'},{l:'Pending Review',v:entries.filter(e=>e.status==='pending').length}].map(s => (
          <div key={s.l}><div style={{ fontFamily:'Orbitron',fontSize:22,fontWeight:800,color:'var(--j1)' }}>{s.v}</div><div className="stat-label">{s.l}</div></div>
        ))}
      </div>
      <PageTurn page={page}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 16 }}>
        {entries.slice(page * PAGE_GRID, (page + 1) * PAGE_GRID).map(e => (
          <div key={e.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 4 }}>{e.title}</div>
                <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>by {e.contributor}</div>
              </div>
              <span className={`pill pill-${e.status}`}>{e.status}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--txt2)', lineHeight: 1.5, marginBottom: 10 }}>{e.description}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
              {(e.tags || []).map(t => <span key={t} style={{ fontSize: 9, padding: '2px 6px', background: 'rgba(0,212,255,0.05)', border: '1px solid var(--brd2)', borderRadius: 2, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{t}</span>)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div><div style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 700, color: 'var(--j3)' }}>{e.reuses}</div><div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>REUSES</div></div>
                {e.timeSavedHours > 0 && <div><div style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 700, color: 'var(--j5)' }}>{e.timeSavedHours}h</div><div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>SAVED</div></div>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {e.status === 'approved' && <button className="btn btn-success btn-sm" onClick={() => { setReuseTarget(e); setReuseForm({ reuseType: '', application: '', hoursSaved: '' }); }}>DECLARE REUSE</button>}
                {can('cwg', 'sme') && e.status === 'pending' && <>
                  <button className="btn btn-success btn-sm" onClick={() => approve(e.id, 'approved')}>APPROVE</button>
                  <button className="btn btn-danger btn-sm" onClick={() => approve(e.id, 'rejected')}>REJECT</button>
                </>}
              </div>
            </div>
          </div>
        ))}
        {entries.length === 0 && <div className="empty-state" style={{ gridColumn: '1/-1' }}>Library is empty. Submit the first entry!</div>}
      </div>
      </PageTurn>
      <Paginate page={page} total={entries.length} pageSize={PAGE_GRID} onPage={setPage} />
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            <div className="modal-title">SUBMIT TO LIBRARY</div>
            {[{f:'title',l:'Title'},{f:'description',l:'Description',ta:true}].map(i => (
              <div key={i.f} className="form-group"><label className="label">{i.l}</label>
                {i.ta ? <textarea className="input" rows={3} value={form[i.f]} onChange={e => setForm(p => ({...p,[i.f]:e.target.value}))} /> : <input className="input" value={form[i.f]} onChange={e => setForm(p => ({...p,[i.f]:e.target.value}))} />}
              </div>
            ))}
            <div className="form-group"><label className="label">Category</label>
              <CategorySelect value={form.category} showPoints={false} onChange={v => setForm(p => ({ ...p, category: v }))} />
            </div>
            {[{f:'tags',l:'Tags (comma separated)'},{f:'documentation',l:'Documentation / Notes',ta:true}].map(i => (
              <div key={i.f} className="form-group"><label className="label">{i.l}</label>
                {i.ta ? <textarea className="input" rows={3} value={form[i.f]} onChange={e => setForm(p => ({...p,[i.f]:e.target.value}))} /> : <input className="input" value={form[i.f]} onChange={e => setForm(p => ({...p,[i.f]:e.target.value}))} />}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowForm(false)}>CANCEL</button>
              <button className="btn btn-primary" onClick={submit}>SUBMIT</button>
            </div>
          </div>
        </div>
      )}

      {/* Reuse declaration modal */}
      {reuseTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <button className="modal-close" onClick={() => setReuseTarget(null)}>×</button>
            <div className="modal-title">REUSE DECLARATION</div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: 'var(--txt)' }}>{reuseTarget.title}</div>
              <div style={{ fontSize: 11, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', marginTop: 2 }}>by {reuseTarget.contributor}</div>
            </div>
            <div className="form-group"><label className="label">How did you reuse it?</label>
              <select className="input" value={reuseForm.reuseType} onChange={e => setReuseForm(p => ({ ...p, reuseType: e.target.value }))}>
                <option value="">Select reuse type…</option>
                {reuseTiers.map(t => <option key={t.id} value={t.id}>{t.label} · +{t.reuser} pts (you) / +{t.originator} (author)</option>)}
              </select>
            </div>
            <div className="form-group"><label className="label">How did you apply it?</label>
              <textarea className="input" rows={3} value={reuseForm.application} onChange={e => setReuseForm(p => ({ ...p, application: e.target.value }))} placeholder="Where and how you used this component, and what it replaced…" /></div>
            <div className="form-group"><label className="label">Estimated time saved (hours)</label>
              <input type="number" min="0" className="input" value={reuseForm.hoursSaved} onChange={e => setReuseForm(p => ({ ...p, hoursSaved: e.target.value }))} placeholder="e.g. 12" /></div>
            <div style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginBottom: 12 }}>CWG verifies reuse declarations. Fabricated declarations are a conduct violation (R&R §8.2).</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setReuseTarget(null)}>CANCEL</button>
              <button className="btn btn-success" onClick={submitReuse}>DECLARE REUSE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── PODS ───────────────────────────────────────────────────────────────────
const POD_NAMES = [
  'GenAI Engineering Pod',
  'Agentic Systems Pod',
  'AI Platform Pod',
  'AI Research Pod',
  'Automation & Optimisation Pod',
  'Backend Systems Pod',
  'Cloud Integration Pod',
  'Data Engineering Pod',
  'DevOps Acceleration Pod',
  'Enablement & Training Pod',
  'Frontend Innovation Pod',
  'Mobile Engineering Pod',
  'Product Strategy Pod',
  'QA & Testing Pod',
  'Security & Compliance Pod',
];

export function Pods() {
  const { can } = useAuth();
  const [pods, setPods] = useState([]);
  const [openPod, setOpenPod] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editPod, setEditPod] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', lead: '', sme: '', members: '', deadline: '', ragStatus: 'green', points: 1000, milestones: '' });
  const [editForm, setEditForm] = useState({ name: '', description: '', lead: '', sme: '', members: '', deadline: '', points: 1000, milestones: '', project: '', client: '' });
  const [nameSearch, setNameSearch] = useState('');
  const [showNameDrop, setShowNameDrop] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type) => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  useEffect(() => { api.get('/pods').then(r => setPods(r.data)).catch(() => {}); }, []);

  const create = async () => {
    try {
      const payload = { ...form, members: form.members.split(',').map(m => m.trim()).filter(Boolean), milestones: form.milestones.split(',').map(m => m.trim()).filter(Boolean), currentMilestone: 0, status: 'active', startDate: new Date().toISOString() };
      const { data } = await api.post('/pods', payload);
      setPods(p => [...p, data]); setShowForm(false); showToast('Pod created!', 'success');
    } catch { showToast('Failed to create pod', 'error'); }
  };

  const openEdit = (pod) => {
    setEditPod(pod);
    setNameSearch(pod.name || '');
    setShowNameDrop(false);
    setEditForm({
      name: pod.name || '',
      description: pod.description || '',
      lead: pod.lead || '',
      sme: pod.sme || '',
      members: (pod.members || []).join(', '),
      deadline: pod.deadline ? pod.deadline.slice(0, 10) : '',
      points: pod.points || 1000,
      milestones: (pod.milestones || []).join(', '),
      project: pod.project || pod.codename || '',
      client: pod.client || '',
    });
  };

  const saveEdit = async () => {
    try {
      const payload = {
        ...editForm,
        members: editForm.members.split(',').map(m => m.trim()).filter(Boolean),
        milestones: editForm.milestones.split(',').map(m => m.trim()).filter(Boolean),
      };
      const { data } = await api.patch(`/pods/${editPod.id}`, payload);
      setPods(p => p.map(pod => pod.id === editPod.id ? data : pod));
      setEditPod(null);
      showToast('Pod updated!', 'success');
    } catch { showToast('Failed to update pod', 'error'); }
  };

  const advanceMilestone = async (id) => {
    try {
      const { data } = await api.patch(`/pods/${id}/milestone`);
      setPods(p => p.map(pod => pod.id === id ? data : pod));
      showToast('Milestone advanced!', 'success');
    } catch { showToast('Failed', 'error'); }
  };

  const updateRag = async (id, ragStatus) => {
    try {
      const { data } = await api.patch(`/pods/${id}`, { ragStatus });
      setPods(p => p.map(pod => pod.id === id ? data : pod));
    } catch {}
  };

  const ragColor = { green: 'var(--j5)', amber: 'var(--j4)', red: 'var(--j6)' };

  return (
    <div>
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div><div className="page-title">POD MANAGEMENT</div><div className="page-sub">Cross-functional teams · Sprint execution · Milestone tracking</div></div>
        {can('cwg') && <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ CREATE POD</button>}
      </div>
      <div style={{ display: 'grid', gap: 16 }}>
        {pods.map(pod => (
          <div key={pod.id} className="card">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--j1)', marginBottom: 6, cursor: 'pointer' }} onClick={() => setOpenPod(pod)} title="View details & charts">{pod.name}</div>
                <div style={{ fontSize: 12, color: 'var(--txt2)' }}>{pod.description}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {can('cwg','sme') && (
                  <select value={pod.ragStatus || 'green'} onChange={e => updateRag(pod.id, e.target.value)} style={{ background: 'transparent', border: `1px solid ${ragColor[pod.ragStatus || 'green']}`, borderRadius: 2, color: ragColor[pod.ragStatus || 'green'], fontFamily: 'Share Tech Mono', fontSize: 10, padding: '3px 6px', cursor: 'pointer' }}>
                    {['green','amber','red'].map(r => <option key={r} value={r} style={{ background: 'var(--bg3)' }}>{r.toUpperCase()}</option>)}
                  </select>
                )}
                {can('cwg') && <button className="btn btn-sm" style={{ borderColor: 'var(--j1)', color: 'var(--j1)', padding: '3px 10px', fontSize: 10 }} onClick={() => openEdit(pod)}>EDIT</button>}
                <span className={`pill pill-${pod.status}`}>{pod.status}</span>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
              {[{l:'Pod Lead',v:pod.lead},{l:'SME Advisor',v:pod.sme},{l:'Start Date',v:new Date(pod.startDate).toLocaleDateString()},{l:'Deadline',v:new Date(pod.deadline).toLocaleDateString()}].map(i => (
                <div key={i.l}><div className="label">{i.l}</div><div style={{ fontSize: 13, color: 'var(--txt)', fontWeight: 600 }}>{i.v}</div></div>
              ))}
            </div>
            <div style={{ marginBottom: 12 }}>
              <div className="label" style={{ marginBottom: 8 }}>Milestone Progress ({pod.currentMilestone}/{pod.milestones?.length})</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {(pod.milestones || []).map((m, i) => (
                  <div key={i} style={{ padding: '4px 10px', borderRadius: 2, border: `1px solid ${i < pod.currentMilestone ? 'rgba(0,255,136,0.4)' : 'var(--brd2)'}`, background: i < pod.currentMilestone ? 'rgba(0,255,136,0.08)' : 'transparent', fontSize: 11, color: i < pod.currentMilestone ? 'var(--j5)' : 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{i < pod.currentMilestone ? '✓ ' : ''}{m}</div>
                ))}
              </div>
              <div style={{ background: 'var(--bg4)', borderRadius: 2, height: 6 }}>
                <div style={{ width: `${Math.round((pod.currentMilestone / (pod.milestones?.length || 1)) * 100)}%`, height: 6, borderRadius: 2, background: 'linear-gradient(90deg, var(--j2), var(--j1))', transition: 'width 0.5s ease' }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {(pod.members || []).map(m => <span key={m} style={{ padding: '2px 8px', border: '1px solid var(--brd2)', borderRadius: 2, fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{m}</span>)}
              </div>
              {can('cwg','sme') && pod.currentMilestone < (pod.milestones?.length || 0) && (
                <button className="btn btn-success btn-sm" onClick={() => advanceMilestone(pod.id)}>COMPLETE MILESTONE</button>
              )}
            </div>
          </div>
        ))}
        {pods.length === 0 && <div className="empty-state">No pods yet. Create the first pod!</div>}
      </div>
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            <div className="modal-title">CREATE NEW POD</div>
            {[{f:'name',l:'Pod Name'},{f:'description',l:'Description',ta:true},{f:'lead',l:'AI Engineer / Pod Lead'},{f:'sme',l:'SME Advisor'},{f:'members',l:'Members (comma separated)'},{f:'milestones',l:'Milestones (comma separated)'}].map(i => (
              <div key={i.f} className="form-group"><label className="label">{i.l}</label>
                {i.ta ? <textarea className="input" rows={2} value={form[i.f]} onChange={e => setForm(p => ({...p,[i.f]:e.target.value}))} /> : <input className="input" value={form[i.f]} onChange={e => setForm(p => ({...p,[i.f]:e.target.value}))} />}
              </div>
            ))}
            <div className="grid-2">
              <div className="form-group"><label className="label">Deadline</label><input type="date" className="input" value={form.deadline} onChange={e => setForm(p => ({...p,deadline:e.target.value}))} /></div>
              <div className="form-group"><label className="label">Bounty Points</label><input type="number" className="input" value={form.points} onChange={e => setForm(p => ({...p,points:+e.target.value}))} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowForm(false)}>CANCEL</button>
              <button className="btn btn-primary" onClick={create}>CREATE POD</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Edit Pod Modal ── */}
      {editPod && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <button className="modal-close" onClick={() => setEditPod(null)}>×</button>
            <div className="modal-title">EDIT POD — {editPod.name}</div>
            {/* Pod Name — role-based combobox, no free text */}
            <div className="form-group">
              <label className="label">Pod Name</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  value={nameSearch}
                  placeholder="Search role-based name..."
                  autoComplete="off"
                  onChange={e => { setNameSearch(e.target.value); setShowNameDrop(true); }}
                  onFocus={() => setShowNameDrop(true)}
                  onBlur={() => setTimeout(() => {
                    setShowNameDrop(false);
                    if (!POD_NAMES.includes(nameSearch)) setNameSearch(editForm.name);
                  }, 150)}
                />
                {showNameDrop && (() => {
                  const hits = POD_NAMES.filter(n => n.toLowerCase().includes(nameSearch.toLowerCase()));
                  return hits.length > 0 ? (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'var(--bg3)', border: '1px solid var(--brd)', borderRadius: 3, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                      {hits.map(n => (
                        <div key={n}
                          style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 12, fontFamily: 'Share Tech Mono', letterSpacing: 0.5, color: n === editForm.name ? 'var(--j1)' : 'var(--txt)', background: n === editForm.name ? 'rgba(0,212,255,0.08)' : 'transparent', borderBottom: '1px solid var(--brd2)', transition: 'background 0.1s' }}
                          onMouseEnter={e => { if (n !== editForm.name) e.currentTarget.style.background = 'rgba(0,212,255,0.04)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = n === editForm.name ? 'rgba(0,212,255,0.08)' : 'transparent'; }}
                          onMouseDown={() => { setEditForm(p => ({ ...p, name: n })); setNameSearch(n); setShowNameDrop(false); }}
                        >
                          {n}
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
              {editForm.name && <div style={{ fontSize: 9, color: 'var(--j5)', fontFamily: 'Share Tech Mono', marginTop: 4, letterSpacing: 1 }}>✓ {editForm.name}</div>}
            </div>
            {/* Other editable fields */}
            {[
              { f: 'project', l: 'Project / Codename' },
              { f: 'client', l: 'Client / Account' },
              { f: 'description', l: 'Description', ta: true },
              { f: 'lead', l: 'Pod Lead (name)' },
              { f: 'sme', l: 'SME Advisor (name)' },
              { f: 'members', l: 'Members (comma separated names)' },
              { f: 'milestones', l: 'Milestones (comma separated)' },
            ].map(i => (
              <div key={i.f} className="form-group">
                <label className="label">{i.l}</label>
                {i.ta
                  ? <textarea className="input" rows={2} value={editForm[i.f]} onChange={e => setEditForm(p => ({ ...p, [i.f]: e.target.value }))} />
                  : <input className="input" value={editForm[i.f]} onChange={e => setEditForm(p => ({ ...p, [i.f]: e.target.value }))} />}
              </div>
            ))}
            <div className="grid-2">
              <div className="form-group"><label className="label">Deadline</label><input type="date" className="input" value={editForm.deadline} onChange={e => setEditForm(p => ({ ...p, deadline: e.target.value }))} /></div>
              <div className="form-group"><label className="label">Bounty Points</label><input type="number" className="input" value={editForm.points} onChange={e => setEditForm(p => ({ ...p, points: +e.target.value }))} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setEditPod(null)}>CANCEL</button>
              <button className="btn btn-primary" onClick={saveEdit}>SAVE CHANGES</button>
            </div>
          </div>
        </div>
      )}
      {openPod && <PodDetail pod={openPod} variant="pod" onClose={() => setOpenPod(null)} />}
    </div>
  );
}

// ── MEMBERS (Admin) ────────────────────────────────────────────────────────
export function Members() {
  const { can } = useAuth();
  const [members, setMembers] = useState([]);
  const [toast, setToast] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const blankForm = { name: '', email: '', password: '', role: 'member', tier: 'Probation' };
  const [form, setForm] = useState(blankForm);
  const showToast = (msg, type) => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const [waitlist, setWaitlist] = useState([]);
  const [openMember, setOpenMember] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // member object
  const [deleting, setDeleting] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const load = () => api.get('/users').then(r => setMembers(r.data)).catch(() => {});
  const loadWaitlist = () => api.get('/waitlist').then(r => setWaitlist(r.data)).catch(() => {});
  useEffect(() => { load(); loadWaitlist(); }, []);

  const setWaitlistStatus = async (id, status) => {
    try {
      await api.patch(`/waitlist/${id}`, { status });
      setWaitlist(p => p.map(w => w.id === id ? { ...w, status } : w));
      showToast(`Waitlist entry marked ${status}`, 'success');
    } catch { showToast('Failed', 'error'); }
  };

  const updateTier = async (id, tier) => {
    try {
      await api.patch(`/users/${id}/tier`, { tier });
      setMembers(p => p.map(m => m.id === id ? { ...m, tier } : m));
      showToast(`Tier updated to ${tier}`, 'success');
    } catch { showToast('Failed', 'error'); }
  };

  const toggleActive = async (m) => {
    try {
      const { data } = await api.patch(`/users/${m.id}/status`, { active: !m.active });
      setMembers(p => p.map(x => x.id === m.id ? { ...x, active: data.active } : x));
      showToast(`${m.name} ${data.active ? 'activated' : 'deactivated'}`, 'success');
    } catch (err) { showToast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      return showToast('Name, email and password are required', 'error');
    }
    if (!isWissenEmail(form.email)) {
      return showToast('Email must be a @wissen.com address', 'error');
    }
    if (form.password.length < 6) {
      return showToast('Password must be at least 6 characters', 'error');
    }
    setSaving(true);
    try {
      const { data } = await api.post('/users', form);
      setMembers(p => [...p, data]);
      showToast(`${data.name} added successfully`, 'success');
      setForm(blankForm);
      setShowAdd(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to add member', 'error');
    }
    setSaving(false);
  };

  const openMemberEdit = (m) => {
    setEditMember(m);
    setEditMemberForm({ name: m.name, email: m.email, role: m.role, tier: m.tier || 'Probation', committeeRole: m.committeeRole || '' });
  };

  const saveMemberEdit = async () => {
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/users/${editMember.id}`, editMemberForm);
      setMembers(p => p.map(m => m.id === editMember.id ? data : m));
      showToast(`${data.name} updated`, 'success');
      setEditMember(null);
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    }
    setEditSaving(false);
  };

  const deleteMember = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteConfirm.id}`);
      setMembers(p => p.filter(m => m.id !== deleteConfirm.id));
      if (openMember === deleteConfirm.id) setOpenMember(null);
      showToast(`${deleteConfirm.name} has been removed`, 'success');
      setDeleteConfirm(null);
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    }
    setDeleting(false);
  };

  const [page, setPage] = useState(0);
  const tierColors = { Probation: '#4fc3f7', Core: '#00ff88', Veteran: '#ffd700', Legend: '#ff6b35' };
  const roleColors = { cwg: 'var(--j3)', stakeholder: 'var(--j4)', sme: 'var(--j1)', member: 'var(--j5)' };

  const committee = members
    .filter(m => m.committeeRole)
    .sort((a, b) => (a.committeeRole === 'Chairperson' ? -1 : b.committeeRole === 'Chairperson' ? 1 : 0));

  const canManage = can('cwg', 'stakeholder');

  return (
    <div>
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title" style={{ color: 'var(--j6)' }}>DELETE MEMBER</div>
            <div style={{ margin: '14px 0 22px', fontSize: 14, color: 'var(--txt2)', lineHeight: 1.6 }}>
              Permanently remove <strong style={{ color: 'var(--txt)' }}>{deleteConfirm.name}</strong> (<span style={{ color: 'var(--txt3)', fontSize: 12 }}>{deleteConfirm.email}</span>) from NeuralOps?
              <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.2)', borderRadius: 4, fontSize: 12, color: 'var(--j6)' }}>
                ⚠ This cannot be undone. Their contributions and history will remain but their login will be removed.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1 }} onClick={() => setDeleteConfirm(null)} disabled={deleting}>CANCEL</button>
              <button className="btn btn-danger" style={{ flex: 2 }} onClick={deleteMember} disabled={deleting}>
                {deleting ? 'DELETING…' : '🗑 DELETE MEMBER'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editMember && (
        <div className="modal-overlay" onClick={() => !editSaving && setEditMember(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setEditMember(null)}>×</button>
            <div className="modal-title">EDIT MEMBER — {editMember.name}</div>
            <div className="form-group">
              <label className="label">Full Name</label>
              <input className="input" value={editMemberForm.name} onChange={e => setEditMemberForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="label">Email</label>
              <input className="input" type="email" value={editMemberForm.email} onChange={e => setEditMemberForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="label">Role</label>
                <select className="input" value={editMemberForm.role} onChange={e => setEditMemberForm(p => ({ ...p, role: e.target.value }))}>
                  {['member', 'sme', 'cwg', 'stakeholder', 'intern'].map(r => <option key={r} value={r} style={{ background: 'var(--bg3)' }}>{r.toUpperCase()}</option>)}
                </select>
              </div>
              {(editMemberForm.role === 'member' || editMemberForm.role === 'intern') && (
                <div className="form-group">
                  <label className="label">Tier</label>
                  <select className="input" value={editMemberForm.tier} onChange={e => setEditMemberForm(p => ({ ...p, tier: e.target.value }))}>
                    {['Probation', 'Core', 'Veteran', 'Legend'].map(t => <option key={t} value={t} style={{ background: 'var(--bg3)' }}>{t}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="label">Committee Role (optional — e.g. Chairperson, Secretary)</label>
              <input className="input" value={editMemberForm.committeeRole} onChange={e => setEditMemberForm(p => ({ ...p, committeeRole: e.target.value }))} placeholder="Leave blank to clear" />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button className="btn" style={{ flex: 1 }} onClick={() => setEditMember(null)} disabled={editSaving}>CANCEL</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={saveMemberEdit} disabled={editSaving}>
                {editSaving ? 'SAVING…' : 'SAVE CHANGES'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div><div className="page-title">MEMBER MANAGEMENT</div><div className="page-sub">CWG & Stakeholder access only</div></div>
        {canManage && (
          <button className="btn btn-primary" onClick={() => setShowAdd(s => !s)} style={{ flexShrink: 0 }}>
            {showAdd ? '× CANCEL' : '+ ADD MEMBER'}
          </button>
        )}
      </div>

      {/* Working Committee */}
      {committee.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'Orbitron', fontSize: 13, fontWeight: 700, color: 'var(--j1)', letterSpacing: 2, marginBottom: 14, textShadow: '0 0 10px var(--glow1)' }}>⬡ WORKING COMMITTEE</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {committee.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'rgba(0,212,255,0.03)', border: `1px solid ${c.committeeRole === 'Chairperson' ? 'var(--j4)' : 'var(--brd2)'}`, borderRadius: 4 }}>
                <Avatar user={c} size={42} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: 'var(--txt)', fontSize: 14 }}>{c.name}</div>
                  <div style={{ fontSize: 10, fontFamily: 'Share Tech Mono', letterSpacing: 1, color: c.committeeRole === 'Chairperson' ? 'var(--j4)' : 'var(--txt2)', textTransform: 'uppercase', marginTop: 2 }}>{c.committeeRole}</div>
                  <div style={{ fontSize: 10, color: 'var(--txt3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waitlist */}
      {canManage && waitlist.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="section-header">
            <div className="section-title" style={{ color: 'var(--j4)' }}>📋 WAITLIST — ACCESS REQUESTS</div>
            <span style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{waitlist.filter(w => w.status === 'pending').length} pending</span>
          </div>
          <table className="table"><thead><tr><th>NAME</th><th>EMAIL</th><th>INTEREST</th><th>STATUS</th><th>ACTIONS</th></tr></thead>
            <tbody>{waitlist.map(w => (
              <tr key={w.id}>
                <td><div style={{ fontWeight: 600, color: 'var(--txt)' }}>{w.name}</div>{w.note && <div style={{ fontSize: 10, color: 'var(--txt3)' }}>{w.note}</div>}</td>
                <td style={{ fontSize: 11, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{w.email}</td>
                <td style={{ fontSize: 11, color: 'var(--txt2)' }}>{w.role}</td>
                <td><span className={`pill pill-${w.status === 'approved' ? 'adopted' : w.status === 'declined' ? 'rejected' : 'pending'}`}>{w.status}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-success btn-sm" onClick={() => setWaitlistStatus(w.id, 'approved')}>APPROVE</button>
                    <button className="btn btn-sm" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setWaitlistStatus(w.id, 'invited')}>INVITE</button>
                    <button className="btn btn-danger btn-sm" onClick={() => setWaitlistStatus(w.id, 'declined')}>DECLINE</button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
          <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginTop: 10 }}>Tip: use “+ ADD MEMBER” above to convert an approved request into an account.</div>
        </div>
      )}

      {/* Add Member form */}
      {showAdd && canManage && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'Orbitron', fontSize: 13, fontWeight: 700, color: 'var(--j1)', letterSpacing: 2, marginBottom: 16 }}>+ ADD NEW MEMBER</div>
          <form onSubmit={addMember}>
            <div className="grid-2">
              <div className="form-group">
                <label className="label">Full Name</label>
                <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Ananya Rao" required />
              </div>
              <div className="form-group">
                <label className="label">Email Address</label>
                <input className="input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="name@wissen.com" required />
              </div>
              <div className="form-group">
                <label className="label">Temporary Password</label>
                <input className="input" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="member shares & resets later" required />
              </div>
              <div className="form-group">
                <label className="label">Role</label>
                <select className="input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="member" style={{ background: 'var(--bg3)' }}>Member</option>
                  <option value="sme" style={{ background: 'var(--bg3)' }}>SME</option>
                  <option value="cwg" style={{ background: 'var(--bg3)' }}>Working Committee (CWG)</option>
                  {can('stakeholder') && <option value="stakeholder" style={{ background: 'var(--bg3)' }}>Stakeholder</option>}
                </select>
              </div>
              {form.role === 'member' && (
                <div className="form-group">
                  <label className="label">Starting Tier</label>
                  <select className="input" value={form.tier} onChange={e => setForm(f => ({ ...f, tier: e.target.value }))}>
                    {['Probation', 'Core', 'Veteran', 'Legend'].map(t => <option key={t} value={t} style={{ background: 'var(--bg3)' }}>{t}</option>)}
                  </select>
                </div>
              )}
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ marginTop: 8, padding: '10px 24px', letterSpacing: 2 }}>
              {saving ? 'CREATING…' : 'CREATE MEMBER'}
            </button>
          </form>
        </div>
      )}

      <PageTurn page={page}>
      <div className="card">
        <table className="table"><thead><tr><th>MEMBER</th><th>ROLE</th><th>TIER</th><th>TOTAL PTS</th><th>WEEKLY PTS</th><th>STATUS</th>{can('cwg','sme') && <th>ACTIONS</th>}</tr></thead>
          <tbody>{members.slice(page * PAGE_TABLE, (page + 1) * PAGE_TABLE).map(m => (
            <tr key={m.id}>
              <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar user={m} size={30} />
                <div><div style={{ fontWeight: 600, color: 'var(--j1)', cursor: 'pointer' }} onClick={() => setOpenMember(m.id)} title="View analytics">{m.name}</div><div style={{ fontSize: 10, color: 'var(--txt2)' }}>{m.email}</div></div>
              </div></td>
              <td><span style={{ padding: '2px 8px', border: '1px solid', borderRadius: 2, fontSize: 9, fontFamily: 'Share Tech Mono', letterSpacing: 1, color: roleColors[m.role], borderColor: roleColors[m.role] + '50', background: roleColors[m.role] + '10' }}>{m.role?.toUpperCase()}</span></td>
              <td>{m.tier && <span className={`tier-badge tier-${m.tier?.toLowerCase()}`}>{m.tier}</span>}</td>
              <td><span style={{ fontFamily: 'Orbitron', fontWeight: 700, color: 'var(--j1)' }}>{(m.totalPoints||0).toLocaleString()}</span></td>
              <td><span style={{ fontFamily: 'Orbitron', fontSize: 12, color: 'var(--j5)' }}>{(m.weeklyPoints||0).toLocaleString()}</span></td>
              <td><span style={{ width: 8, height: 8, borderRadius: '50%', background: m.active ? 'var(--j5)' : 'var(--j6)', display: 'inline-block' }} /></td>
              {can('cwg','sme') && <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {m.role === 'member' && (
                  <select value={m.tier || ''} onChange={e => updateTier(m.id, e.target.value)} style={{ background: 'transparent', border: '1px solid var(--brd2)', borderRadius: 2, color: tierColors[m.tier] || 'var(--txt2)', fontFamily: 'Share Tech Mono', fontSize: 10, padding: '3px 6px', cursor: 'pointer' }}>
                    {['Probation','Core','Veteran','Legend'].map(t => <option key={t} value={t} style={{ background: 'var(--bg3)' }}>{t}</option>)}
                  </select>
                )}
                {canManage && (
                  <button className={`btn btn-sm ${m.active ? 'btn-danger' : 'btn-success'}`} onClick={() => toggleActive(m)} title={m.active ? 'Deactivate member' : 'Activate member'}>
                    {m.active ? 'DEACTIVATE' : 'ACTIVATE'}
                  </button>
                )}
                {can('cwg') && (
                  <button className="btn btn-sm" onClick={() => openMemberEdit(m)} title="Edit member"
                    style={{ borderColor: 'rgba(0,212,255,0.4)', color: 'var(--j1)', padding: '3px 8px' }}>
                    ✎
                  </button>
                )}
                {can('cwg') && m.role !== 'cwg' && (
                  <button className="btn btn-sm" onClick={() => setDeleteConfirm(m)} title="Delete member"
                    style={{ borderColor: 'rgba(255,68,68,0.4)', color: '#ff6666', padding: '3px 8px' }}>
                    🗑
                  </button>
                )}
                </div>
              </td>}
            </tr>
          ))}</tbody>
        </table>
      </div>
      </PageTurn>
      <Paginate page={page} total={members.length} pageSize={PAGE_TABLE} onPage={setPage} />
      {openMember && <MemberDetail id={openMember} onClose={() => setOpenMember(null)} onDeleted={id => { setMembers(p => p.filter(m => m.id !== id)); setOpenMember(null); }} />}
    </div>
  );
}

// ── FAQ ────────────────────────────────────────────────────────────────────
export function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get('/ai/faq').then(r => setFaqs(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (search.length > 1) api.get(`/ai/faq?q=${encodeURIComponent(search)}`).then(r => setFaqs(r.data)).catch(() => {});
    else if (!search) api.get('/ai/faq').then(r => setFaqs(r.data)).catch(() => {});
  }, [search]);

  const categories = ['membership', 'points', 'quests', 'process', 'governance', 'events'];

  return (
    <div>
      <div style={{ marginBottom: 24 }}><div className="page-title">FAQ — KNOWLEDGE BASE</div><div className="page-sub">All answers compiled from official NeuralOps programme documents</div></div>
      <div style={{ marginBottom: 20 }}>
        <input className="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search FAQs — type a keyword like 'points', 'mentor', 'kill bonus'..." style={{ fontSize: 13 }} />
      </div>
      <div style={{ marginBottom: 12, fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>{faqs.length} answers found · Source: NeuralOps Programme Documents v1.0</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {faqs.map((f, i) => (
          <div key={i} className="card" style={{ cursor: 'pointer' }} onClick={() => setOpen(open === i ? null : i)}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: open === i ? 'var(--j1)' : 'var(--txt)', flex: 1 }}>{f.q}</div>
              <span style={{ color: 'var(--j1)', fontSize: 18, flexShrink: 0, marginLeft: 12 }}>{open === i ? '−' : '+'}</span>
            </div>
            {open === i && (
              <div style={{ marginTop: 12, fontSize: 13, color: 'var(--txt2)', lineHeight: 1.7, borderTop: '1px solid var(--brd2)', paddingTop: 12 }}>{f.a}</div>
            )}
          </div>
        ))}
        {faqs.length === 0 && (
          <div className="empty-state">
            No FAQs match your search. Try asking JARVIS (click "ASK JARVIS" in the sidebar) for personalised answers!
          </div>
        )}
      </div>
      <div className="card" style={{ marginTop: 24, background: 'linear-gradient(135deg, rgba(0,212,255,0.06), rgba(0,153,255,0.03))' }}>
        <div style={{ fontFamily: 'Orbitron', fontSize: 14, fontWeight: 700, color: 'var(--j1)', marginBottom: 8 }}>⬡ CAN'T FIND YOUR ANSWER?</div>
        <div style={{ fontSize: 13, color: 'var(--txt2)', marginBottom: 12 }}>JARVIS, the NeuralOps AI assistant, is trained on all programme documents and can answer any specific question. Click "ASK JARVIS" in the sidebar to start a conversation.</div>
        <div style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Sources: NeuralOps R&R Programme v1.0 · Team Structure & Operating Model v1.0 · Engagement Calendar FY 2026–27</div>
      </div>
    </div>
  );
}
